import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { projectToPillarData, TabDefinition, PILLAR_ACCENT_COLORS } from '../../lib/pillarTypes';
import {
  getProjectOverride,
  setProjectOverride,
  resolveCommunityAncestors,
} from '../../Data/ProjectConfig/projectConfigOverrides';
import { communityConfigStore } from '../../Data/store';
import PillarRelationshipButtons from '../../Components/PillarPage/PillarRelationshipButtons';
import ProjectConfigModal from '../../Components/ProjectConfigModal/ProjectConfigModal';
// DEV-841 / DEV-840 Path A: direct-from-thermometer wizard launcher.
// Listens for 'l4e_open_kpi_config' window events fired by Schedule and
// Budget thermometer pencils and opens the wizard tray DIRECTLY (no
// ProjectConfigModal hop). Save persists via the same project KPI
// override API ProjectConfigModal uses for its own commit.
import { KpiWizardLauncher } from '../../Components/KpiWizardLauncher/KpiWizardLauncher';
import CoreTabPanel from './CoreTabPanel';
import TeamListPage from '../../Components/ProjectTeam/TeamListPage';
import ExpertTabPanel from './ExpertTabPanel';
import CommunityTabPanel from './CommunityTabPanel';
import WorkTabPanel from './WorkTabPanel';
import ImpactTabPanel from './ImpactTabPanel';
import MediaTabPanel from './MediaTabPanel';
import { canAdminProject, canEditProjectContent, canViewProject } from '../../security/securityAdapter';
import { useApiProjectRoles } from '../../api/useApiProjectRoles';
import type { ViewerTier } from '../../Components/ProjectTeam/projectTeamTypes';
import type { ProjectViewMode } from './projectViewMode';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { uploadDataUrl, fileUrl } from '../../api/fileStorage';
import { mediaSrc } from '../../api/mediaUrl';
import { fetchOwnerDisplay } from '../../api/ownerDisplay';
// DEV-932: securityUsers now sourced from the API via useApiSecurityData.
import { useApiSecurityData } from '../../api/useApiSecurityData';
// DEV-965: community entities are needed to distinguish user-owned from
// community-owned projects before the owner-avatar fetch.
import { useApiCommunityData } from '../../api/useApiCommunityData';
import WorkspaceCards from '../../Components/WorkspaceCards/WorkspaceCards';
import { buildWorkspaceCardItems } from '../../Components/WorkspaceCards/workspaceCardItems';
import styles from './ProjectDetail.module.css';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/** Resolved dynamically below since the Management tab is conditionally included. */
// (WORK_TAB_INDEX removed — resolved from the dynamic tabs array)

const MAX_RAW_FILE_BYTES = 10 * 1024 * 1024;
const JPEG_QUALITY = 0.92;
const COMPACT_HERO_HEIGHT = 200;
const CROP_OUTPUT_W = 1920;
const L4E_LOGO = `${process.env.PUBLIC_URL}/L4EBlendLogo.png`;

/* Country name → flag emoji lookup (mirrors PillarPageHeader) */
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  'united states': 'US', 'usa': 'US', 'united states of america': 'US',
  'canada': 'CA', 'france': 'FR', 'germany': 'DE', 'united kingdom': 'GB',
  'australia': 'AU', 'japan': 'JP', 'china': 'CN', 'india': 'IN',
  'brazil': 'BR', 'mexico': 'MX', 'spain': 'ES', 'italy': 'IT',
  'netherlands': 'NL', 'sweden': 'SE', 'norway': 'NO', 'denmark': 'DK',
  'finland': 'FI', 'ukraine': 'UA', 'poland': 'PL', 'russia': 'RU',
  'south korea': 'KR', 'new zealand': 'NZ', 'south africa': 'ZA',
  'argentina': 'AR', 'colombia': 'CO', 'chile': 'CL', 'peru': 'PE',
  'egypt': 'EG', 'nigeria': 'NG', 'kenya': 'KE', 'ghana': 'GH',
  'morocco': 'MA', 'saudi arabia': 'SA', 'israel': 'IL', 'turkey': 'TR',
  'ireland': 'IE', 'portugal': 'PT', 'switzerland': 'CH', 'austria': 'AT',
  'belgium': 'BE', 'greece': 'GR', 'czech republic': 'CZ', 'czechia': 'CZ',
  'romania': 'RO', 'hungary': 'HU', 'singapore': 'SG', 'malaysia': 'MY',
  'indonesia': 'ID', 'thailand': 'TH', 'vietnam': 'VN', 'philippines': 'PH',
  'pakistan': 'PK', 'bangladesh': 'BD', 'sri lanka': 'LK', 'nepal': 'NP',
  'taiwan': 'TW', 'hong kong': 'HK', 'iceland': 'IS', 'luxembourg': 'LU',
  'costa rica': 'CR', 'panama': 'PA', 'uruguay': 'UY', 'ecuador': 'EC',
  'bolivia': 'BO', 'paraguay': 'PY', 'venezuela': 'VE', 'jamaica': 'JM',
  'trinidad and tobago': 'TT', 'dominican republic': 'DO', 'cuba': 'CU',
  'guatemala': 'GT', 'honduras': 'HN', 'el salvador': 'SV', 'nicaragua': 'NI',
  'ethiopia': 'ET', 'tanzania': 'TZ', 'uganda': 'UG', 'rwanda': 'RW',
  'senegal': 'SN', 'cameroon': 'CM', 'algeria': 'DZ', 'tunisia': 'TN',
  'libya': 'LY', 'jordan': 'JO', 'iraq': 'IQ', 'iran': 'IR',
  'united arab emirates': 'AE', 'qatar': 'QA', 'kuwait': 'KW', 'oman': 'OM',
  'bahrain': 'BH', 'lebanon': 'LB', 'syria': 'SY', 'yemen': 'YE',
};

function countryFlagEmoji(code?: string): string {
  if (!code || code.length !== 2) return '';
  const pts = code.toUpperCase().split('').map((c) => 0x1f1e6 + c.charCodeAt(0) - 65);
  return String.fromCodePoint(...pts);
}

function resolveFlag(codeOrName?: string): string {
  if (!codeOrName) return '';
  if (codeOrName.length === 2) return countryFlagEmoji(codeOrName);
  const code = COUNTRY_NAME_TO_CODE[codeOrName.toLowerCase().trim()];
  return code ? countryFlagEmoji(code) : '';
}

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function compactTimeline(start?: string, end?: string): string {
  if (!start && !end) return '';
  const parse = (d?: string) => { if (!d) return null; const dt = new Date(d); return isNaN(dt.getTime()) ? null : dt; };
  const s = parse(start);
  const e = parse(end);
  if (s && e) {
    const sm = SHORT_MONTHS[s.getMonth()]; const sy = s.getFullYear();
    const em = SHORT_MONTHS[e.getMonth()]; const ey = e.getFullYear();
    return sy === ey ? `${sm} \u2013 ${em} ${ey}` : `${sm} ${sy} \u2013 ${em} ${ey}`;
  }
  if (s) return `${SHORT_MONTHS[s.getMonth()]} ${s.getFullYear()} \u2013`;
  if (e) return `\u2013 ${SHORT_MONTHS[e!.getMonth()]} ${e!.getFullYear()}`;
  return '';
}

/* ------------------------------------------------------------------ */
/*  CropOverlay (compact variant — 200 px viewport)                    */
/* ------------------------------------------------------------------ */

interface CropState {
  src: string;
  naturalW: number;
  naturalH: number;
}

function CropOverlay({
  crop,
  onConfirm,
  onCancel,
  /** Target display aspect ratio (w/h) — output canvas matches this so
   *  object-fit: cover doesn't further zoom the cropped result. */
  targetAspect,
}: {
  crop: CropState;
  onConfirm: (dataUrl: string) => void;
  onCancel: () => void;
  targetAspect?: number;
}) {
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const [viewportW, setViewportW] = React.useState(400);
  const [viewportH, setViewportH] = React.useState(COMPACT_HERO_HEIGHT);
  const [zoom, setZoom] = React.useState(1);
  const [pan, setPan] = React.useState({ x: 0, y: 0 });
  const dragRef = React.useRef<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null);

  // Measure the overlay's parent container (heroBanner) so the output
  // canvas matches its aspect ratio and object-fit:cover won't re-zoom.
  const computedTargetAspect = React.useMemo(() => {
    if (targetAspect && targetAspect > 0) return targetAspect;
    const parent = overlayRef.current?.parentElement;
    if (parent && parent.clientWidth > 0 && parent.clientHeight > 0) {
      return parent.clientWidth / parent.clientHeight;
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetAspect, viewportW]); // viewportW as proxy for "layout settled"

  React.useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setViewportW(e.contentRect.width);
        setViewportH(e.contentRect.height);
      }
    });
    ro.observe(el);
    setViewportW(el.clientWidth);
    setViewportH(el.clientHeight || COMPACT_HERO_HEIGHT);
    return () => ro.disconnect();
  }, []);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const { naturalW, naturalH } = crop;
  const baseScale = Math.max(viewportW / naturalW, viewportH / naturalH);
  const displayScale = baseScale * zoom;
  const displayW = naturalW * displayScale;
  const displayH = naturalH * displayScale;

  const clampPan = React.useCallback(
    (px: number, py: number, z?: number) => {
      const ds = baseScale * (z ?? zoom);
      const dw = naturalW * ds;
      const dh = naturalH * ds;
      const maxPanX = Math.max(0, (dw - viewportW) / 2);
      const maxPanY = Math.max(0, (dh - viewportH) / 2);
      return {
        x: Math.max(-maxPanX, Math.min(maxPanX, px)),
        y: Math.max(-maxPanY, Math.min(maxPanY, py)),
      };
    },
    [baseScale, naturalW, naturalH, viewportW, viewportH, zoom],
  );

  const onPointerDown = React.useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
  }, [pan]);

  const onPointerMove = React.useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan(clampPan(dragRef.current.startPanX + dx, dragRef.current.startPanY + dy));
  }, [clampPan]);

  const onPointerUp = React.useCallback(() => { dragRef.current = null; }, []);

  const onWheel = React.useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const next = Math.max(1, Math.min(4, zoom - e.deltaY * 0.002));
    setZoom(next);
    setPan((p) => clampPan(p.x, p.y, next));
  }, [zoom, clampPan]);

  const handleZoomSlider = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseFloat(e.target.value);
    setZoom(next);
    setPan((p) => clampPan(p.x, p.y, next));
  }, [clampPan]);

  const handleConfirm = React.useCallback(() => {
    // The crop UI viewport (viewportW × viewportH) may differ from the
    // target display area because the overlay has header/footer bars.
    // If a targetAspect (w/h) is provided we expand the crop region to
    // match the banner so object-fit:cover won't zoom further.
    const aspect = computedTargetAspect && computedTargetAspect > 0 ? computedTargetAspect : viewportW / viewportH;

    // Source region visible in the viewport
    const visSrcW = viewportW / displayScale;
    const visSrcH = viewportH / displayScale;
    const visSrcCX = (displayW / 2 - pan.x) / displayScale; // center X in natural coords
    const visSrcCY = (displayH / 2 - pan.y) / displayScale; // center Y in natural coords

    // Expand/contract the source region to match targetAspect
    let srcW: number, srcH: number;
    if (visSrcW / visSrcH > aspect) {
      // viewport is wider than target — expand height
      srcW = visSrcW;
      srcH = visSrcW / aspect;
    } else {
      // viewport is taller than target — expand width
      srcH = visSrcH;
      srcW = visSrcH * aspect;
    }

    // Clamp to natural image bounds
    const srcX = Math.max(0, Math.min(naturalW - srcW, visSrcCX - srcW / 2));
    const srcY = Math.max(0, Math.min(naturalH - srcH, visSrcCY - srcH / 2));

    const outW = CROP_OUTPUT_W;
    const outH = Math.round(CROP_OUTPUT_W / aspect);
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
      onConfirm(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.src = crop.src;
  }, [crop.src, naturalW, naturalH, displayW, displayH, displayScale, pan, viewportW, viewportH, computedTargetAspect, onConfirm]);

  const imgStyle: React.CSSProperties = {
    position: 'absolute',
    width: displayW,
    height: displayH,
    left: (viewportW - displayW) / 2 + pan.x,
    top: (viewportH - displayH) / 2 + pan.y,
    userSelect: 'none',
    pointerEvents: 'none',
  };

  return (
    <div ref={overlayRef} style={{
      position: 'absolute', inset: 0, zIndex: 20,
      background: 'var(--bg-elevated)',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '6px 10px', fontSize: 11, fontWeight: 600,
        color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>Drag to reposition &middot; scroll to zoom</span>
        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{Math.round(zoom * 100)}%</span>
      </div>
      <div
        ref={viewportRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
        style={{
          flex: 1, position: 'relative', overflow: 'hidden',
          cursor: dragRef.current ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        <img src={crop.src} alt="Crop preview" draggable={false} style={imgStyle} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
          {[1, 2].map((i) => (
            <React.Fragment key={i}>
              <div style={{ position: 'absolute', left: `${(i / 3) * 100}%`, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.15)' }} />
              <div style={{ position: 'absolute', top: `${(i / 3) * 100}%`, left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.15)' }} />
            </React.Fragment>
          ))}
        </div>
      </div>
      <div style={{
        padding: '6px 10px', background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 10, color: 'var(--text-tertiary)', flexShrink: 0 }}>Zoom</span>
        <input
          type="range" min="1" max="4" step="0.01"
          value={zoom} onChange={handleZoomSlider}
          style={{ flex: 1, accentColor: 'var(--accent-secondary)', cursor: 'pointer' }}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" onClick={onCancel} style={{
            background: 'var(--bg-hover)', border: '1px solid var(--border-default)',
            borderRadius: 5, color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600,
            padding: '4px 10px', cursor: 'pointer',
          }}>Cancel</button>
          <button type="button" onClick={handleConfirm} style={{
            background: 'rgba(69,187,71,0.75)', border: '1px solid var(--border-default)',
            borderRadius: 5, color: 'var(--text-primary)', fontSize: 11, fontWeight: 600,
            padding: '4px 10px', cursor: 'pointer',
          }}>Save Crop</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ProjectDetailPanelProps {
  /** The full Data-array row for this project (includes computed KPIs + raw project). */
  projectRow: any;
  currentUserGuid: string;
  debugMode?: boolean;
  onProjectConfigChanged?: () => void;
  /** Called when user clicks the close button. */
  onClose?: () => void;
  /**
   * KPI tab index from the grid click (0=Summary, 1=Schedule, 2=Finance, 3=Mission, 4=Data).
   * When provided (>= 0), the panel opens on the Performance tab at the matching KPI sub-tab.
   * When -1 or undefined, the panel opens on the Core tab (default).
   */
  kpiTabIndex?: number;
  /** Monotonic counter that bumps on every grid click so the panel can detect
   *  repeated clicks on the same project / tab combination. */
  tabRequestId?: number;
  /**
   * Fires when the user changes the KPI tab inside the detail panel's Performance view.
   * Used for bidirectional sync — the grid highlights the corresponding KPI shape.
   */
  onKpiTabChange?: (index: number) => void;
  /**
   * DEV-649: Context-aware view mode. Controls layout, tab visibility, default
   * tab, and data restrictions.
   * - 'leader':    Management subtabs only, compact header.
   * - 'portfolio': KPI shapes only, Official data, compact header.
   * - 'team':      Full view, default to Impact tab.
   * - 'member':    Full view, default to About tab.
   * Defaults to 'member' when not specified.
   */
  viewMode?: ProjectViewMode;
  /** Initial work sub-tab to restore when returning from full view.
   *  When provided, the Management tab opens on this sub-section. */
  initialWorkSubTab?: 'performance' | 'resources' | 'budget' | 'team' | 'contributions' | 'risksIssues';
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ProjectDetailPanel({
  projectRow,
  currentUserGuid,
  debugMode,
  onProjectConfigChanged,
  onClose,
  kpiTabIndex,
  tabRequestId,
  onKpiTabChange,
  viewMode = 'member',
  initialWorkSubTab,
}: ProjectDetailPanelProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // DEV-932: API-sourced security users (for the sysadmin-owner check below).
  const { securityUsers } = useApiSecurityData();
  // DEV-895 Phase 2 / DEV-965 — community entities + relationships feed
  // fetchOwnerDisplay, which resolves community-vs-user, walks the
  // logo-inheritance cascade, and returns the right avatar.
  const {
    communityEntities: ownerCheckCommunityEntities,
    communityRelationships: ownerCheckRelationships,
  } = useApiCommunityData();

  /* ── DEV-649: ViewMode-derived flags ── */
  const isCompactView = viewMode === 'leader' || viewMode === 'portfolio';
  const isLeaderView = viewMode === 'leader';
  const isPortfolioView = viewMode === 'portfolio';

  /* ---- Derive PillarData from the raw project inside the Data row ---- */
  // rawProject: the underlying project record (for pillarData, ProjectConfigModal).
  // enrichedRow: the full KPI-enriched Data row (for KpiNavigation header tab shapes).
  const rawProject = projectRow?.project ?? projectRow;
  const enrichedRow = projectRow; // preserves schedule_indicator_color etc.
  const projectGuid = String(projectRow?.project_guid ?? rawProject?.project_GUID ?? '');

  const pillarData = useMemo(
    () => projectToPillarData(rawProject),
    [rawProject]
  );

  /* ---- Owner avatar ---- */
  // DEV-895 Phase 2 / DEV-983 — Removed the isSysAdmin-of-the-viewer
  // short-circuit. It conflated viewer-sysadmin (irrelevant) with
  // owner-is-sysadmin (the intent) because the fallback used
  // currentUserGuid. Every sysadmin viewing a community-owned project
  // hit the short-circuit and skipped the cascade. fetchOwnerDisplay
  // now handles every case uniformly.

  // DEV-895 Phase 2 / DEV-982 — see ProjectDetailPage for rationale on
  // why this is a key-based ref, not a boolean guard.
  const ownerResolvedForKey = useRef<string>('');
  const [ownerAvatarUrl, setOwnerAvatarUrl] = useState<string | null>(null);
  // DEV-991: see ProjectDetailPage for rationale.
  const [ownerInfo, setOwnerInfo] = useState<{ kind: 'user' | 'community' | 'unknown'; guid: string; displayName: string } | null>(null);

  useEffect(() => {
    if (!projectGuid) return;
    let cancelled = false;

    // DEV-895 Phase 2 / DEV-982 — Resolve target owner GUID from the
    // override blob first, then the canonical project.community_id, then
    // primary_owner_org_guid (legacy rows). We deliberately do NOT fall
    // back to currentUserGuid — that gave the logged-in user's avatar
    // for every project they viewed, regardless of ownership.
    let targetGuid: string | undefined;
    try {
      const ov = getProjectOverride(projectGuid) as any;
      targetGuid = ov?.owner_guid;
    } catch { /* no override */ }
    if (!targetGuid) {
      const rp = rawProject as any;
      targetGuid = rp?.community_id || rp?.owning_community_entity_guid || rp?.primary_owner_org_guid;
    }
    if (!targetGuid) return;

    const entitiesLoaded = (ownerCheckCommunityEntities ?? []).length > 0;
    const key = `${targetGuid}|${entitiesLoaded ? 'loaded' : 'empty'}`;
    if (ownerResolvedForKey.current === key) return;
    ownerResolvedForKey.current = key;

    const tg = targetGuid;
    fetchOwnerDisplay(tg, ownerCheckCommunityEntities, ownerCheckRelationships)
      .then((display) => {
        if (cancelled) return;
        if (display.avatarUrl) setOwnerAvatarUrl(display.avatarUrl);
        // DEV-991 — record owner kind/guid for clickable avatar.
        setOwnerInfo({ kind: display.kind, guid: tg, displayName: display.displayName });
      })
      .catch(() => { /* fallback to initial letter */ });

    return () => { cancelled = true; };
  }, [projectGuid, rawProject, ownerCheckCommunityEntities, ownerCheckRelationships]);

  /* ---- Tray display data ---- */
  /** Community path segments with guid + active status for linking. */
  const communityPath = useMemo(() => {
    const locGuid = String(rawProject?.primary_location_entity_guid ?? '').trim();
    if (!locGuid) {
      // Fallback to plain string names (no links)
      return [pillarData.communityL3, pillarData.communityL2, pillarData.communityL1]
        .filter(Boolean)
        .map((name) => ({ guid: '', name: name as string, isActive: false }));
    }
    const ancestors = resolveCommunityAncestors(locGuid);
    // Build leaf + ancestors in leaf-to-root order
    const leafName = pillarData.communityL3 || pillarData.communityL2 || pillarData.communityL1 || '';
    const leafGuid = locGuid.toUpperCase();
    const leafConfig = communityConfigStore.get(leafGuid);
    const leafActive = leafConfig?.is_active === true;
    const segments: { guid: string; name: string; isActive: boolean }[] = [];
    if (leafName) {
      segments.push({ guid: leafGuid, name: leafName, isActive: leafActive });
    }
    for (let i = ancestors.length - 1; i >= 0; i--) {
      const a = ancestors[i];
      const cfg = communityConfigStore.get(a.guid.toUpperCase());
      segments.push({ guid: a.guid, name: a.name, isActive: cfg?.is_active === true });
    }
    return segments;
  }, [pillarData, rawProject]);
  const flagEmoji = resolveFlag(pillarData.countryFlagCode) || resolveFlag(pillarData.communityL1);
  const timeline = compactTimeline(pillarData.startDate, pillarData.endDate);

  /* ---- Hero image ---- */
  // Resolve initial hero URL from store / pillarData
  const resolveHeroUrl = useCallback((): string | null => {
    if (!projectGuid) return null;
    try {
      const ov = getProjectOverride(projectGuid) as any;
      if (ov?.tile_image_url) return mediaSrc(ov.tile_image_url as string);
      if (ov?.logo_url) return mediaSrc(ov.logo_url as string);
    } catch { /* ignore */ }
    return mediaSrc(pillarData?.heroImageUrl || pillarData?.logoUrl || null);
  }, [projectGuid, pillarData]);

  // Direct state for the hero URL — avoids indirection through store reads in useMemo
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(resolveHeroUrl);

  // Sync from external changes (e.g. pillarData updated via API refetch)
  useEffect(() => {
    const resolved = resolveHeroUrl();
    if (resolved) setHeroImageUrl(resolved);
  }, [resolveHeroUrl]);

  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const [cropState, setCropState] = useState<CropState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canEditHero = canAdminProject(currentUserGuid, projectGuid);
  const canAdminProj = canEditHero; // same call, reuse
  const canEditProject = canEditProjectContent(currentUserGuid, projectGuid);
  const { slots: roleSlots } = useApiProjectRoles(projectGuid);

  /* DEV-1050 cutover complete — shadow-mode V1↔V2 parity observer removed
     along with the V1 evaluator. V2 is authoritative; no comparison surface
     remains. */

  const isTeamMember = useMemo(() => {
    if (canAdminProj) return true;
    const ug = currentUserGuid.toUpperCase();
    return roleSlots.some((slot) =>
      (slot.assignments ?? []).some(
        (a) =>
          a.userGuid.toUpperCase() === ug &&
          (a.status === 'active' || a.status === 'pending_verification' || a.status === 'pending_escalated_review')
      )
    );
  }, [canAdminProj, currentUserGuid, roleSlots]);

  const viewerTier: ViewerTier = canAdminProj
    ? 'admin'
    : isTeamMember
      ? 'team'
      : 'public';

  const showManagementTab = canAdminProj || canEditProject || isTeamMember;
  const canEditManagement = canAdminProj || canEditProject;

  const [heroMenuOpen, setHeroMenuOpen] = useState(false);
  const heroMenuRef = useRef<HTMLDivElement>(null);

  // Close hero menu on click outside
  useEffect(() => {
    if (!heroMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (heroMenuRef.current && !heroMenuRef.current.contains(e.target as Node)) {
        setHeroMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [heroMenuOpen]);

  const persistHeroUrl = useCallback(
    (url: string) => {
      if (!projectGuid) return;
      // Update store for persistence / API write-through
      setProjectOverride(projectGuid, { tile_image_url: url });
      // DIRECTLY set the displayed URL — no store round-trip needed
      setHeroImageUrl(url);
      onProjectConfigChanged?.();
    },
    [projectGuid, onProjectConfigChanged],
  );

  const handleHeroFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please select an image file.'); return; }
    if (file.size > MAX_RAW_FILE_BYTES) { alert(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 10 MB.`); return; }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { setCropState({ src: objectUrl, naturalW: img.naturalWidth, naturalH: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); alert('Failed to load image.'); };
    img.src = objectUrl;
    e.target.value = '';
  }, []);

  const handleCropConfirm = useCallback((dataUrl: string) => {
    if (cropState) URL.revokeObjectURL(cropState.src);
    setCropState(null);
    // Upload cropped image to MinIO file storage, fall back to data URL if unavailable
    if (projectGuid) {
      const ts = Date.now();
      uploadDataUrl(dataUrl, 'heroes', projectGuid, `hero-${ts}.jpg`)
        .then((result) => {
          // Append cache-buster so the browser always fetches the new image
          const storedUrl = `${fileUrl(result.key)}?v=${ts}`;
          console.log('[Hero] Uploaded to MinIO:', storedUrl);
          persistHeroUrl(storedUrl);
        })
        .catch((err) => {
          console.warn('[Hero] MinIO upload failed, falling back to data URL:', err);
          persistHeroUrl(dataUrl);
        });
    } else {
      persistHeroUrl(dataUrl);
    }
  }, [cropState, persistHeroUrl, projectGuid]);

  const handleCropCancel = useCallback(() => {
    if (cropState) URL.revokeObjectURL(cropState.src);
    setCropState(null);
  }, [cropState]);

  /** Open crop overlay on the existing hero image.
   *  Fetches the image as a blob so the canvas is never tainted by cross-origin
   *  pixels (avoids SecurityError on toDataURL even when the storage server
   *  omits Access-Control-Allow-Origin headers). */
  const handleEditHero = useCallback(() => {
    if (!heroImageUrl) return;
    fetch(heroImageUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          setCropState({ src: blobUrl, naturalW: img.naturalWidth, naturalH: img.naturalHeight });
        };
        img.onerror = () => {
          URL.revokeObjectURL(blobUrl);
          alert('Could not load the current hero image for editing.');
        };
        img.src = blobUrl;
      })
      .catch(() => { alert('Could not load the current hero image for editing.'); });
  }, [heroImageUrl]);

  const handleHeroUrlSubmit = useCallback(() => {
    const url = urlDraft.trim();
    if (!url) return;
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
      alert('Please enter a valid URL starting with http:// or https://');
      return;
    }
    // If it's a data URL, upload to MinIO first
    if (url.startsWith('data:') && projectGuid) {
      uploadDataUrl(url, 'heroes', projectGuid, 'hero.jpg')
        .then((result) => {
          persistHeroUrl(fileUrl(result.key));
        })
        .catch(() => {
          persistHeroUrl(url); // fallback
        });
    } else {
      persistHeroUrl(url);
    }
    setShowUrlInput(false);
    setUrlDraft('');
  }, [urlDraft, persistHeroUrl, projectGuid]);

  const handleRemoveHero = useCallback(() => {
    if (!projectGuid) return;
    setProjectOverride(projectGuid, { tile_image_url: '' });
    setHeroImageUrl(null);
    onProjectConfigChanged?.();
  }, [projectGuid, onProjectConfigChanged]);

  /* ---- Tab state ---- */
  // kpiTabIndex convention from the grid:
  //   -1 (or <0) = project name clicked → open Core tab
  //   0+ = KPI shape clicked → open Work tab at that KPI sub-tab
  //   0 = Summary, 1 = Schedule, 2 = Finance, 3 = Mission, 4 = Data
  const hasKpiRequest = kpiTabIndex != null && kpiTabIndex >= 0;
  // Management tab is index 0 when visible; when hidden, there's no Work tab
  const workTabIndex = showManagementTab ? 0 : -1;

  /* DEV-649: Resolve default tab index based on viewMode.
   * - leader / portfolio (compact): always 0 (only Management tab is in the array).
   * - team: Impact tab (index 1 if Management is visible, 0 if not).
   * - member: About tab (index 2 if Management is visible, 1 if not). */
  const viewModeDefaultIndex = useMemo(() => {
    if (hasKpiRequest && workTabIndex >= 0) return workTabIndex;
    if (isCompactView) return 0;
    if (viewMode === 'team') {
      // Impact tab: after Management (if visible)
      return showManagementTab ? 1 : 0;
    }
    // member: About tab: after Management + Impact
    return showManagementTab ? 2 : 1;
  }, [hasKpiRequest, workTabIndex, isCompactView, viewMode, showManagementTab]);

  const [activeTabIndex, setActiveTabIndex] = useState(viewModeDefaultIndex);

  /* Track the KPI tab to pass into the Work tab's Performance view */
  const [workKpiTab, setWorkKpiTab] = useState(hasKpiRequest ? kpiTabIndex : 0);

  /* Work sub-tab state (Performance / Resources / Team) and Gantt modal.
   * Initialise from prop when returning from full view to restore user context. */
  const [workSubTab, setWorkSubTab] = useState<'performance' | 'resources' | 'budget' | 'team' | 'contributions' | 'risksIssues'>(initialWorkSubTab ?? 'performance');
  const [ganttOpen, setGanttOpen] = useState(false);
  const [showTeamList, setShowTeamList] = useState(false);
  const handleOpenGantt = useCallback(() => setGanttOpen(true), []);
  const handleCloseGantt = useCallback(() => setGanttOpen(false), []);

  /* i18n for tab labels */
  const { t: tKpi } = useAppTranslation('kpi');
  const { t: tPages } = useAppTranslation('pages');

  /* When the parent signals a new tab request (tabRequestId bumps),
   * decide whether to show Core or a specific KPI tab. */
  const prevRequestIdRef = useRef(tabRequestId);
  useEffect(() => {
    if (tabRequestId == null || tabRequestId === prevRequestIdRef.current) return;
    prevRequestIdRef.current = tabRequestId;
    if (kpiTabIndex != null && kpiTabIndex >= 0) {
      // KPI shape clicked → jump to Performance tab at the requested KPI sub-tab
      setActiveTabIndex(workTabIndex >= 0 ? workTabIndex : 0);
      setWorkKpiTab(kpiTabIndex);

      // Auto-scroll past the hero photo so the title bar, main tab bar,
      // and sub-tab bar all dock at the top and the KPI content is
      // visible immediately below them.
      requestAnimationFrame(() => {
        const scrollEl = panelScrollRef.current;
        const tabBarEl = stickyTabBarRef.current;
        if (scrollEl && tabBarEl) {
          // The tab bar's offsetTop is its position in the document flow
          // (below the title bar + hero). When we scroll so that the tab
          // bar reaches its sticky offset (--title-bar-h = 44px), it
          // docks. The exact scroll amount is:
          //   tabBar.offsetTop − titleBarHeight
          // This leaves the tab bar docked at 44px and the content
          // starting right below the sub-tab bar.
          const titleBarH = 44; // matches --title-bar-h
          const scrollTarget = Math.max(0, tabBarEl.offsetTop - titleBarH);
          scrollEl.scrollTo({ top: scrollTarget, behavior: 'auto' });
        }
      });
    } else {
      // Project name clicked (kpiTabIndex = -1) → jump to Core
      setActiveTabIndex(0);
      // Scroll to top so the hero photo is fully visible
      requestAnimationFrame(() => {
        const scrollEl = panelScrollRef.current;
        if (scrollEl) {
          scrollEl.scrollTo({ top: 0, behavior: 'auto' });
        }
      });
    }
  }, [tabRequestId, kpiTabIndex]);

  /* ── Refs ── */
  const heroBannerRef = useRef<HTMLDivElement>(null);
  const panelScrollRef = useRef<HTMLDivElement>(null);
  const stickyTabBarRef = useRef<HTMLDivElement>(null);

  /* ---- Edit project modal ---- */
  const [isEditOpen, setIsEditOpen] = useState(false);
  const handleEdit = useCallback(() => setIsEditOpen(true), []);
  const handleEditClose = useCallback(() => setIsEditOpen(false), []);
  const handleEditSaved = useCallback(() => {
    setIsEditOpen(false);
    // Re-resolve hero URL from store in case the edit changed it
    setHeroImageUrl(resolveHeroUrl());
    onProjectConfigChanged?.();
  }, [onProjectConfigChanged, resolveHeroUrl]);

  /* ---- "View Full" — navigate to the canonical full project page ----
   * Available from compact views (leader, portfolio). Hidden on full views.
   * DEV-649 / DEV-655: Context-aware return state. */
  const handleViewFull = useMemo(() => {
    if (!isCompactView) return null;
    return () => {
      /* Compact views only show the Management (work) tab, so the current
       * tab key is always 'work'.  We preserve workSubTab so the full page
       * opens on the same sub-section (Performance, Team, etc.). */
      const currentTabKey = 'work';
      const returnLabel = isLeaderView ? 'Projects' : 'Portfolio';
      navigate(`/projects/${projectGuid}/${currentTabKey}`, {
        state: {
          returnTo: location.pathname,
          returnLabel,
          restoreProjectGuid: projectGuid,
          workSubTab,
        },
      });
    };
  }, [isCompactView, isLeaderView, navigate, projectGuid, location.pathname, workSubTab]);

  /* ---- Tab definitions ---- */
  /* Tab order: Management (gated), Impact, Core, Expert, Community, Media.
   * DEV-649: Compact views (leader/portfolio) show only the Management tab.
   * Full views (team/member) show all entitled tabs. */
  const managementTabDef: TabDefinition = useMemo(() => ({
    key: 'work',
    label: 'Management',
    content: (
      <WorkTabPanel
        data={pillarData}
        projectPayload={enrichedRow}
        currentUserGuid={currentUserGuid}
        canEdit={canEditManagement}
        debugMode={debugMode}
        onProjectConfigChanged={onProjectConfigChanged}
        kpiTabIndex={workKpiTab}
        onKpiTabChange={onKpiTabChange}
        activeSubTab={workSubTab}
        onSubTabChange={setWorkSubTab}
        ganttOpen={ganttOpen}
        onCloseGantt={handleCloseGantt}
        hideSubTabBar
        forceOfficialOnly={isPortfolioView}
      />
    ),
  }), [pillarData, enrichedRow, currentUserGuid, canEditManagement, debugMode, onProjectConfigChanged, workKpiTab, onKpiTabChange, workSubTab, ganttOpen, handleCloseGantt, isPortfolioView]);

  const tabs: TabDefinition[] = useMemo(() => {
    /* Compact views: Management tab only (leader gets full management subtabs,
       portfolio gets KPI shapes — controlled by WorkTabPanel's forceOfficialOnly). */
    if (isCompactView) {
      return showManagementTab ? [managementTabDef] : [];
    }

    /* Full views (team / member): all entitled tabs */
    return [
      ...(showManagementTab ? [managementTabDef] : []),
      {
        key: 'impact',
        label: 'Impact',
        content: <ImpactTabPanel data={pillarData} projectGuid={projectGuid || ''} currentUserGuid={currentUserGuid} />,
      },
      {
        key: 'about',
        label: 'About',
        content: <CoreTabPanel data={pillarData} projectGuid={projectGuid} viewerTier={viewerTier} onShowTeamList={() => setShowTeamList(true)} />,
      },
      {
        key: 'expert',
        label: 'Expert',
        content: <ExpertTabPanel data={pillarData} />,
      },
      {
        key: 'community',
        label: 'Community',
        content: <CommunityTabPanel data={pillarData} />,
      },
      {
        key: 'media',
        label: 'Media',
        content: <MediaTabPanel projectGuid={projectGuid} currentUserGuid={currentUserGuid} debugMode={debugMode} />,
      },
    ];
  }, [isCompactView, showManagementTab, managementTabDef, pillarData, projectGuid, currentUserGuid, viewerTier, debugMode]);

  const activeTab = tabs[activeTabIndex];

  /* ---- Hero edit controls (rendered inside the compact banner) ---- */
  const heroEditControls = canEditHero ? (
    <>
      {/* Crop overlay */}
      {cropState && (
        <CropOverlay crop={cropState} onConfirm={handleCropConfirm} onCancel={handleCropCancel} />
      )}

      {!cropState && !heroImageUrl && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-muted)', fontSize: 13, gap: 6, zIndex: 2,
        }}>
          <span style={{ fontSize: 28, opacity: 0.4 }}>&#128247;</span>
          <span>No hero image</span>
        </div>
      )}
      {!cropState && (
        <div ref={heroMenuRef} style={{ position: 'absolute', top: 8, insetInlineEnd: 8, zIndex: 50 }}>
          <button type="button" onClick={() => setHeroMenuOpen((v) => !v)} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            background: '#fff', color: '#1e293b',
            border: 'none', borderRadius: 7, padding: '5px 11px',
            fontSize: 11, fontWeight: 600, cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            transition: 'background 0.15s',
          }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--text-primary)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Edit cover photo
          </button>

          {heroMenuOpen && (
            <div style={{
              position: 'absolute', top: '100%', insetInlineEnd: 0,
              marginTop: 6,
              background: 'var(--bg-elevated)', backdropFilter: 'blur(8px)',
              borderRadius: 10, overflow: 'hidden',
              border: '1px solid var(--border-default)',
              boxShadow: '0 8px 28px rgba(0,0,0,0.5)', minWidth: 170,
              zIndex: 60,
            }}>
              <button type="button" onClick={() => { fileInputRef.current?.click(); setHeroMenuOpen(false); }} style={compactHeroMenuItemStyle}>
                {heroImageUrl ? 'Change Photo' : 'Upload Photo'}
              </button>
              {heroImageUrl && (
                <button type="button" onClick={() => { handleEditHero(); setHeroMenuOpen(false); }} style={compactHeroMenuItemStyle}>
                  Edit Crop
                </button>
              )}
              <button type="button" onClick={() => {
                setShowUrlInput((v) => !v); setUrlDraft(''); setHeroMenuOpen(false);
              }} style={compactHeroMenuItemStyle}>
                Paste URL
              </button>
              {heroImageUrl && (
                <button type="button" onClick={() => { handleRemoveHero(); setHeroMenuOpen(false); }} style={{
                  ...compactHeroMenuItemStyle, color: '#f87171', borderBottom: 'none',
                }}>
                  Remove Photo
                </button>
              )}
            </div>
          )}

          {showUrlInput && !heroMenuOpen && (
            <div style={{
              position: 'absolute', top: '100%', insetInlineEnd: 0,
              marginTop: 6, display: 'flex', gap: 4, maxWidth: 320,
            }}>
              <input
                type="text"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleHeroUrlSubmit();
                  if (e.key === 'Escape') { setShowUrlInput(false); setUrlDraft(''); }
                }}
                placeholder="https://example.com/image.jpg"
                autoFocus
                style={{
                  flex: 1, background: 'rgba(0,0,0,0.70)',
                  border: '1px solid var(--border-subtle)', borderRadius: 5,
                  color: 'var(--text-secondary)', fontSize: 11, padding: '4px 8px', outline: 'none', minWidth: 140,
                }}
              />
              <button type="button" onClick={handleHeroUrlSubmit} style={{
                background: 'rgba(69,187,71,0.70)', border: '1px solid var(--border-default)',
                borderRadius: 5, color: 'var(--text-primary)', fontSize: 11, fontWeight: 600,
                padding: '4px 10px', cursor: 'pointer',
              }}>Save</button>
            </div>
          )}
        </div>
      )}
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleHeroFileChange} />
    </>
  ) : null;

  if (showTeamList && projectGuid) {
    return (
      <div className={styles.detailPanel}>
        <TeamListPage
          projectGuid={projectGuid}
          projectName={pillarData?.name || 'Project'}
          viewerTier={viewerTier}
          onBack={() => setShowTeamList(false)}
        />
      </div>
    );
  }

  return (
    <div className={styles.detailPanel} style={{ '--pillar-accent': PILLAR_ACCENT_COLORS.projects } as React.CSSProperties}>
      {/* Single scroll container — hero, title, tab bar, and content all
          flow together. The title bar and tab bar are position: sticky so
          they dock at the top as the hero scrolls away. */}
      <div ref={panelScrollRef} className={styles.panelScroll}>
        {/* ── DEV-649: Compact header for Leader/Portfolio views ── */}
        {isCompactView && (
          <div className={styles.compactHeader}>
            <div className={styles.compactHeaderInner}>
              {heroImageUrl ? (
                <img src={heroImageUrl} alt="" className={styles.compactHeroThumb} />
              ) : (
                <div className={styles.compactHeroThumbFallback}>
                  {pillarData.name.charAt(0).toUpperCase()}
                </div>
              )}
              <h2 className={styles.compactHeaderName}>{pillarData.name}</h2>
              <div className={styles.compactHeaderActions}>
                {canEditHero && handleEdit && (
                  // Gear, not pencil. Same swap shipped in PillarPageHeader
                  // earlier this session — pencil reads as "edit content,"
                  // gear is the right semantic for "configure how this thing
                  // behaves." Project Settings opens a configuration panel,
                  // not a content editor. The split-screen surface gets the
                  // same treatment so users see consistent affordances
                  // whether they're in compact or full view.
                  <button type="button" className={styles.panelActionBtn} onClick={handleEdit} title={tPages('projectDetail.projectSettings')} aria-label={tPages('projectDetail.projectSettings')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                  </button>
                )}
                {handleViewFull && (
                  <button type="button" className={styles.compactViewFullBtn} onClick={handleViewFull}>
                    {tPages('projectDetail.viewFull')}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="21" y1="3" x2="14" y2="10" />
                    </svg>
                  </button>
                )}
                {onClose && (
                  <button type="button" className={styles.panelActionBtn} onClick={onClose} title="Close" aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Full hero with H2 card tray overlay (hidden in compact views) ── */}
        {!isCompactView && <div
          ref={heroBannerRef}
          className={styles.heroOuter}
          style={heroMenuOpen ? { overflow: 'visible' } : undefined}
        >
          {heroImageUrl ? (
            <>
              <div className={styles.heroBleed} style={{ backgroundImage: `url(${heroImageUrl})` }} />
              <div className={styles.heroBanner} style={heroMenuOpen ? { overflow: 'visible', zIndex: 20 } : undefined}>
                <img
                  src={heroImageUrl}
                  alt={`${pillarData.name} hero`}
                  className={styles.heroBannerImg}
                />
                {/* Gradient overlay for tray readability */}
                <div className={styles.heroGradient} />
              </div>
            </>
          ) : (
            <div className={styles.heroBanner} style={{ background: 'linear-gradient(135deg, var(--accent-primary, #3b82f6)88 0%, var(--bg-secondary) 100%)' }}>
              <div className={styles.heroGradient} />
            </div>
          )}

          {/* ── Top-right: action buttons in frosted pill + cover photo edit ── */}
          <div className={styles.panelTopRight}>
            <div className={styles.panelActions}>
              {canEditHero && handleEdit && (
                // Gear, not pencil — same change as the compact-header
                // counterpart above. See comment there for rationale.
                <button type="button" className={styles.panelActionBtn} onClick={handleEdit} title={tPages('projectDetail.projectSettings')} aria-label={tPages('projectDetail.projectSettings')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>
              )}
              {handleViewFull && (
                <button type="button" className={styles.panelActionBtn} onClick={handleViewFull} title={tPages('projectDetail.viewFull')} aria-label={tPages('projectDetail.viewFull')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                </button>
              )}
              {onClose && (
                <button type="button" className={styles.panelActionBtn} onClick={onClose} title="Close" aria-label="Close">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            {heroEditControls && (
              <div className={styles.panelHeroEditInline}>{heroEditControls}</div>
            )}
          </div>

          {/* ── Card tray (avatar + name + community path + metadata) ── */}
          <div className={styles.panelTray}>
            <div className={styles.panelTrayTop}>
              {/* DEV-991 — Clickable owner avatar.
                  User owner → /profile/{guid} with initialTab='about' in
                  location.state (DEV-1455: profile Impact tab moved to My Impact). Community owner → /communities/{guid}#alignment. */}
              <div
                className={styles.panelTrayAvatar}
                role={ownerInfo && ownerInfo.kind !== 'unknown' ? 'link' : undefined}
                tabIndex={ownerInfo && ownerInfo.kind !== 'unknown' ? 0 : undefined}
                title={ownerInfo && ownerInfo.kind !== 'unknown' ? `Open ${ownerInfo.displayName || 'owner'}` : undefined}
                style={{ cursor: ownerInfo && ownerInfo.kind !== 'unknown' ? 'pointer' : 'default' }}
                onClick={() => {
                  if (!ownerInfo || ownerInfo.kind === 'unknown') return;
                  if (ownerInfo.kind === 'user') {
                    navigate(`/profile/${ownerInfo.guid}`, { state: { initialTab: 'about' } });
                  } else {
                    navigate(`/communities/${ownerInfo.guid}#alignment`);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  if (!ownerInfo || ownerInfo.kind === 'unknown') return;
                  e.preventDefault();
                  if (ownerInfo.kind === 'user') {
                    navigate(`/profile/${ownerInfo.guid}`, { state: { initialTab: 'about' } });
                  } else {
                    navigate(`/communities/${ownerInfo.guid}#alignment`);
                  }
                }}
              >
                {ownerAvatarUrl ? (
                  <img src={mediaSrc(ownerAvatarUrl) ?? undefined} alt="" />
                ) : pillarData.logoUrl ? (
                  <img src={mediaSrc(pillarData.logoUrl) ?? undefined} alt="" />
                ) : (
                  pillarData.name.charAt(0).toUpperCase()
                )}
              </div>
              <div className={styles.panelTrayTitleGroup}>
                <h2 className={styles.panelTrayName}>{pillarData.name}</h2>
                {communityPath.length > 0 && (
                  <div className={styles.panelTrayCommunity}>
                    {flagEmoji && <span className={styles.panelTrayFlag}>{flagEmoji}</span>}
                    {communityPath.map((seg, i) => (
                      <React.Fragment key={seg.guid || seg.name}>
                        {i > 0 && <span className={styles.panelTrayArrow}>{'\u203A'}</span>}
                        {seg.isActive && seg.guid ? (
                          <span
                            role="link"
                            tabIndex={0}
                            className={styles.panelTrayCommunityLink}
                            onClick={() => navigate(`/communities/${seg.guid}`)}
                            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/communities/${seg.guid}`); }}
                          >
                            {seg.name}
                          </span>
                        ) : (
                          <span>{seg.name}</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.panelTrayDivider} />
            <div className={styles.panelTrayCard}>
              <div>
                <div className={styles.panelTrayLabel}>{tPages('projectDetail.tray.category')}</div>
                <div className={styles.panelTrayValue}>{pillarData.category || '\u2014'}</div>
              </div>
              <div>
                <div className={styles.panelTrayLabel}>{tPages('projectDetail.tray.status')}</div>
                <div className={styles.panelTrayValue}>{pillarData.status || '\u2014'}</div>
              </div>
              <div>
                <div className={styles.panelTrayLabel}>{tPages('projectDetail.tray.timeline')}</div>
                <div className={styles.panelTrayValue}>{timeline || '\u2014'}</div>
              </div>
              <div>
                <div className={styles.panelTrayLabel}>{tPages('projectDetail.tray.content')}</div>
                <div className={styles.panelTrayValue}>{pillarData.contentStatus || '\u2014'}</div>
              </div>
            </div>
          </div>
        </div>}

        {/* ── Tab bar — sticky below title bar.
              When Work is active, a second row of sub-tabs renders
              inside the same sticky block so there is zero gap.
              DEV-649: In compact views, the main tab row is hidden
              (only one tab) — sub-tab cards render directly. ── */}
        <div ref={stickyTabBarRef} className={styles.stickyTabBar}>
          {/* Main tab row — hidden in compact views (only 1 tab = Management) */}
          {!isCompactView && <div className={styles.mainTabRow} role="tablist">
            {tabs.map((tab, i) => {
              const isActive = i === activeTabIndex;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`${styles.panelTab} ${isActive ? styles.panelTabActive : ''}`}
                  onClick={() => setActiveTabIndex(i)}
                  style={
                    isActive
                      ? ({ '--tab-accent': 'var(--accent-primary)' } as React.CSSProperties)
                      : undefined
                  }
                >
                  {tab.label}
                </button>
              );
            })}
          </div>}

          {/* Work sub-tab row — color-coded workspace cards (I-1 design).
              DEV-649: Hidden in portfolio view — portfolio managers see only
              Performance (KPI shapes), no sub-tab navigation. */}
          {activeTab?.key === 'work' && !isPortfolioView && (
            <WorkspaceCards
              items={buildWorkspaceCardItems(
                {
                  performance: tPages('projectDetail.management.performance'),
                  // D1 (Resources audit 2026-05-22): label now matches the tab
                  // key. Previously rendered as "Pre-Flight" via tKpi('preflight.title'),
                  // which diverged from both the tab key 'resources' and the
                  // platform's umbrella vocabulary (LeftNav Contribute → Resources,
                  // Mini-Project Resources section, Impact Resources panel).
                  // The underlying data model is still preflight_items; only
                  // the label changed. See POLICY_RESOURCE_VOCABULARY.md (TBD).
                  resources: tPages('projectDetail.management.resources'),
                  budget: tPages('budget.tabTitle'),
                  team: tPages('projectDetail.management.team'),
                  contributions: tPages('projectDetail.management.contributions'),
                  risksIssues: tPages('projectDetail.management.risksIssues'),
                  gantt: tPages('gantt.title'),
                },
                { ganttDisabled: !projectGuid },
              )}
              activeKey={workSubTab === 'performance' || workSubTab === 'resources' || workSubTab === 'budget' || workSubTab === 'team' || workSubTab === 'contributions' || workSubTab === 'risksIssues' ? workSubTab : 'performance'}
              onSelect={(key) => {
                if (key === 'gantt') {
                  handleOpenGantt();
                } else {
                  setWorkSubTab(key as 'performance' | 'resources' | 'budget' | 'team' | 'contributions' | 'risksIssues');
                }
              }}
            />
          )}
        </div>

        {/* ── Tab content ── */}
        <div
          className={styles.panelTabContent}
          role="tabpanel"
          id={activeTab ? `panel-${activeTab.key}` : undefined}
          aria-labelledby={activeTab ? `tab-${activeTab.key}` : undefined}
        >
          {activeTab?.content}
        </div>

        {/* Spacer: ensures enough scroll room for the hero to scroll
            completely off-screen even when tab content is short. */}
        <div aria-hidden className={styles.scrollSpacer} />
      </div>

      {/* Edit project modal */}
      <ProjectConfigModal
        isOpen={isEditOpen}
        onClose={handleEditClose}
        project={rawProject}
        onSaved={handleEditSaved}
      />

      {/* DEV-841 / DEV-840 Path A: thermometer pencil → wizard tray, direct.
          Listens for `l4e_open_kpi_config` window events scoped to this
          project and opens the appropriate wizard. Save commits straight
          to the project KPI override API — no modal hop, no two-Save trap. */}
      <KpiWizardLauncher projectGuid={projectGuid} onSaved={handleEditSaved} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Compact hero dropdown menu item style                              */
/* ------------------------------------------------------------------ */

const compactHeroMenuItemStyle: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'start',
  background: 'transparent', border: 'none',
  borderBottom: '1px solid var(--border-subtle)',
  color: 'var(--text-tertiary)', fontSize: 13, fontWeight: 500,
  padding: '10px 16px', cursor: 'pointer',
  transition: 'background 0.15s, color 0.15s',
};

